import { useEffect, useMemo, useState } from 'react';
import Select, { InputActionMeta, SingleValue } from 'react-select';
import { useTranslation } from 'react-i18next';
import type { MotornetAutosuggestMatch, MotornetVehicleOption } from '../types';
import {
	MIN_MANUFACTURE_CODE_LENGTH,
	useVehicleAutosuggest,
} from './useVehicleAutosuggest';
import { productionFetchParams } from './selectorMeta';

interface MotornetVehicleSelectProps {
	onSelect?: (option: MotornetVehicleOption | null) => void;
	value?: MotornetVehicleOption | null;
	placeholder?: string;
	isDisabled?: boolean;
	debounceMs?: number;
}

const optionFromMatch = (
	match: MotornetAutosuggestMatch
): MotornetVehicleOption => ({
	value: match.codiceEurotax,
	label: `${match.marca.nome} ${match.versione} · ${match.codiceCostruttore}`,
	manufactureCode: match.codiceCostruttore,
	vehicleKind: match.vehicleKind,
	match,
	...productionFetchParams(match),
});

const MotornetVehicleSelect = ({
	onSelect,
	value,
	placeholder,
	isDisabled,
	debounceMs = 400,
}: MotornetVehicleSelectProps) => {
	const { t } = useTranslation();
	const [code, setCode] = useState('');

	const debouncedSetCode = useMemo(() => {
		let timer: ReturnType<typeof setTimeout>;
		const run = (next: string) => {
			clearTimeout(timer);
			timer = setTimeout(() => setCode(next), debounceMs);
		};
		run.cancel = () => clearTimeout(timer);
		return run;
	}, [debounceMs]);
	useEffect(() => () => debouncedSetCode.cancel(), [debouncedSetCode]);

	const { data, isFetching, isError } = useVehicleAutosuggest(code);

	const options = useMemo<MotornetVehicleOption[]>(
		() => (data?.matches ?? []).map(optionFromMatch),
		[data]
	);

	const handleInputChange = (input: string, meta: InputActionMeta) => {
		if (meta.action === 'input-change') {
			debouncedSetCode(input.trim());
		}
	};

	const noOptionsMessage = () => {
		if (code.length < MIN_MANUFACTURE_CODE_LENGTH) {
			return t(
				'eurotax_vehicleSelect_typeMore',
				'Digita un codice costruttore'
			);
		}
		if (isError) {
			return t('eurotax_vehicleSelect_notFound', 'Nessun veicolo trovato');
		}
		return t('eurotax_vehicleSelect_noResults', 'Nessun risultato');
	};

	return (
		<Select<MotornetVehicleOption>
			classNamePrefix="eurotax-vehicle-select"
			menuPosition="fixed"
			isClearable
			isDisabled={isDisabled}
			isLoading={isFetching}
			options={options}
			value={value}
			filterOption={null}
			onInputChange={handleInputChange}
			onChange={(option: SingleValue<MotornetVehicleOption>) =>
				onSelect?.(option ?? null)
			}
			placeholder={
				placeholder ??
				t('eurotax_vehicleSelect_placeholder', 'Cerca per codice costruttore')
			}
			loadingMessage={() => t('eurotax_vehicleSelect_loading', 'Ricerca…')}
			noOptionsMessage={noOptionsMessage}
		/>
	);
};

export default MotornetVehicleSelect;
